/**
 * The context panel's first line: which agent answers this episode, which
 * model it uses, and what the last turn's parameters came to.
 *
 * Plan: docs/plans/episode-agent-parity.md S8; layout in
 * docs/backend/episodes.md ("[Target] The context panel").
 *
 * The agent is chosen while the episode is empty and frozen by its first
 * message — an episode is a conversation with ONE agent. So after the freeze
 * the control is disabled rather than hidden, and still says what was chosen:
 * a control that disappears reads as the app forgetting.
 *
 * The model may change at any time, within the family its agent can reach.
 */
import { useEffect, useState, type JSX } from "react";
import type { TurnResolution } from "@magnis/client-core";
import { ContextMenuSurface, Icon } from "../components/ui";

export interface AgentChoice {
  readonly id: string;
  readonly label: string;
}

export interface EpisodeAgentHeaderProps {
  readonly engine: string | null;
  readonly engineLocked: boolean;
  readonly engines: readonly string[];
  /** Models this engine may be asked for — `id` is the catalogue row, `label`
   *  the provider's wire name. They differ, and the row is what is stored. */
  readonly models: readonly AgentChoice[];
  readonly model: string | null;
  readonly lastTurnResolution?: TurnResolution | null;
  /** Why the last choice was refused. The backend refuses a frozen engine and
   *  an unrunnable model on purpose; not showing it leaves a control that
   *  looks broken. */
  readonly error?: string | null;
  readonly onSelectEngine: (engine: string) => void;
  readonly onSelectModel: (model: string | null) => void;
}

/** One line of the acknowledgement, or null when there is nothing to say. */
function stepsLabel(resolution: TurnResolution | null | undefined): string | null {
  const steps = resolution?.steps;
  if (!steps) return null;
  if ("unsupported" in steps) return "steps n/a";
  // The requested number is not what happened. Showing it would hide the
  // clamp, which is the defect this whole acknowledgement exists to fix.
  return `${String(steps.effective)} steps`;
}

/** A label that opens the design system's menu.
 *
 * The menu itself is `ContextMenuSurface` — the same surface the rest of the
 * app uses, which brings `role="menuitemradio"`, a checked marker and the
 * shared styling. The hand-rolled dropdown this replaces had none of those,
 * and closed only on its own toggle, so both pickers could sit open at once.
 */
function MenuButton({
  label,
  disabled,
  testId,
  options,
  selectedId,
  onPick,
}: {
  readonly label: string;
  readonly disabled: boolean;
  readonly testId: string;
  readonly options: readonly AgentChoice[];
  readonly selectedId: string | null;
  readonly onPick: (id: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);

  // Escape and a click anywhere else close it — the two affordances every
  // other menu in the app has and this one did not.
  useEffect(() => {
    if (!open) return;
    const close = (): void => { setOpen(false); };
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", close);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", close);
    };
  }, [open]);

  return (
    <div className="relative" onMouseDown={(e) => { e.stopPropagation(); }}>
      <button
        type="button"
        data-testid={testId}
        disabled={disabled}
        aria-disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => { setOpen(!open); }}
        className={`flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs ${
          disabled
            ? "cursor-default text-[var(--color-agent-center-text-muted)]"
            : "cursor-pointer text-[var(--color-agent-center-text)] hover:bg-[var(--color-agent-plan-header-border)]/40"
        }`}
      >
        <span>{label}</span>
        {!disabled && <Icon name="chevron-down" size={12} />}
      </button>
      {open && !disabled && (
        <div className="absolute left-0 top-full z-10 mt-1" data-testid={`${testId}-menu`}>
          <ContextMenuSurface
            items={options.map((option) => ({
              id: option.id,
              label: option.label,
              role: "menuitemradio" as const,
              ariaChecked: option.id === selectedId,
            }))}
            onSelect={(id) => {
              setOpen(false);
              onPick(id);
            }}
          />
        </div>
      )}
    </div>
  );
}

export function EpisodeAgentHeader({
  engine,
  engineLocked,
  engines,
  models,
  model,
  lastTurnResolution,
  error,
  onSelectEngine,
  onSelectModel,
}: EpisodeAgentHeaderProps): JSX.Element {
  // A row id is not a label. If the episode points at a row this engine's
  // list does not contain, saying so is honest; printing the id pretends an
  // opaque string is a model name.
  const chosen = models.find((m) => m.id === model);
  const modelLabel =
    chosen?.label ?? (model === null ? "default model" : "unknown model");
  const steps = stepsLabel(lastTurnResolution);

  return (
    <div
      data-testid="episode-agent-header"
      className="flex items-center gap-1 border-b border-agent-border px-3 py-2 text-xs text-[var(--color-agent-center-text-muted)]"
    >
      <MenuButton
        label={engine ?? "no agent"}
        disabled={engineLocked}
        testId="episode-engine-picker"
        options={engines.map((name) => ({ id: name, label: name }))}
        selectedId={engine}
        onPick={onSelectEngine}
      />
      <span aria-hidden="true">·</span>
      <MenuButton
        label={modelLabel}
        // The model stays changeable for the episode's whole life; only an
        // episode with no engine yet has nothing to filter a list by.
        disabled={engine === null}
        testId="episode-model-picker"
        options={[{ id: "", label: "engine default" }, ...models]}
        selectedId={model ?? ""}
        onPick={(id) => { onSelectModel(id === "" ? null : id); }}
      />
      {steps !== null && (
        <span className="ml-auto" data-testid="episode-turn-steps">
          {steps}
        </span>
      )}
      {error != null && error !== "" && (
        <span
          className="ml-auto max-w-[60%] truncate text-red"
          title={error}
          data-testid="episode-agent-error"
        >
          {error}
        </span>
      )}
    </div>
  );
}
